// The /looks screen (#105): the editor + the committed-Looks library. LooksPage is the
// orchestrator — it owns the owner's cameras, the selected camera, the Commit action, and the
// debounced Test-look save (the tethered live-mirror a Look-Sync tap pulls). The editor renders
// a "ship bar" (camera picker + Commit) so "which camera" and "commit this" stay in view while
// authoring, and the library below is just the committed list for that camera.

import { useEffect, useRef, useState } from "react";
import type { ApiClient, Device } from "../api";
import { isUnauthorized } from "../api";
import { LookPortal, DEFAULT_LOOK, type EditableLook } from "./LookPortal";
import { LookLibrary } from "./LookLibrary";
import { editableLookToWire, wireLookToEditable } from "./lookWire";

/** A typed status line — success vs failure render differently and announce via aria-live. */
export interface LookStatus { kind: "ok" | "error"; text: string; }

/** Field-by-field Look equality (key order varies by how a Look was built, so don't JSON-compare).
 *  MUST cover every committed field — the Selective keys + Vignette (optional, `?? 0` to match the
 *  wire/DEFAULT_LOOK defaults) included, else the dirty dot and the replace-your-edits guard go
 *  blind to those sliders and silently discard real edits. */
function sameLook(a: EditableLook, b: EditableLook): boolean {
  return a.name === b.name && a.style === b.style && a.mapMode === b.mapMode
    && a.brightness === b.brightness && a.contrast === b.contrast && a.saturation === b.saturation
    && a.gamma === b.gamma && a.posterize === b.posterize && a.hueRotate === b.hueRotate
    && a.negative === b.negative && a.solarize === b.solarize
    && a.ditherStrength === b.ditherStrength && a.ditherBias === b.ditherBias
    && a.glyphSet === b.glyphSet && a.glyphSize === b.glyphSize
    && (a.lumaKey ?? 0) === (b.lumaKey ?? 0)
    && (a.chromaKey ?? 0) === (b.chromaKey ?? 0)
    && (a.splash ?? 0) === (b.splash ?? 0)
    && (a.vignette ?? 0) === (b.vignette ?? 0)
    && (a.vignetteRadius ?? 0) === (b.vignetteRadius ?? 0)
    && (a.vignetteFeather ?? 0) === (b.vignetteFeather ?? 0)
    && a.colors.length === b.colors.length && a.colors.every((c, i) => c === b.colors[i]);
}

export function LooksPage({ client, seed }: { client: ApiClient; seed?: { url: string; isClip?: boolean } }): React.JSX.Element {
  // The Look currently shown in the editor (reported via onLookChange).
  const [look, setLook] = useState<EditableLook | null>(null);
  // A committed Look the library asked to re-open in the editor (a fresh object each load re-fires).
  const [loadLook, setLoadLook] = useState<EditableLook | undefined>(undefined);
  // The owner's claimed cameras + the selected one (shared by the editor ship bar + the library).
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");
  // A non-auth device-fetch failure. Without it a network blip left devices [] and the page
  // read "No claimed cameras yet" — indistinguishable from owning nothing, with no way back.
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [devicesNonce, setDevicesNonce] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [commitMsg, setCommitMsg] = useState<LookStatus | null>(null);
  // Bumped after a commit so the library re-lists.
  const [committedAt, setCommittedAt] = useState(0);
  // The tethered Test-look mirror status (a Look-Sync tap pulls the latest edit to the camera).
  const [testLook, setTestLook] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  // Dirty-guard: the last saved/loaded Look (baseline) vs the editor's current Look. Editing past
  // the baseline confirms before re-opening a library Look would discard the work.
  const [baseline, setBaseline] = useState<EditableLook | null>(null);
  const [pendingLoad, setPendingLoad] = useState<EditableLook | null>(null);
  // Mirror the live editor state so the ASYNC Test-look resume can read the LATEST clean/dirty status —
  // its effect closure would otherwise capture stale state and could clobber an in-flight edit.
  const editStateRef = useRef<{ look: EditableLook | null; baseline: EditableLook | null }>({ look: null, baseline: null });
  editStateRef.current = { look, baseline };

  // The owner's claimed cameras (re-run by Retry via devicesNonce). (Unclaimed/revoked can't
  // hold Looks.) A retry must not clobber an already-picked camera, hence the `cur ||` seed.
  useEffect(() => {
    let live = true;
    client
      .getDevices()
      .then((ds) => {
        if (!live) return;
        setDevicesError(null);
        const claimed = ds.filter((d) => d.status === "claimed");
        setDevices(claimed);
        if (claimed[0]) setDeviceId((cur) => cur || claimed[0]!.deviceId);
      })
      .catch((e) => {
        if (!live) return;
        if (isUnauthorized(e)) setCommitMsg({ kind: "error", text: "Sign in to manage Looks." });
        else setDevicesError(e instanceof Error ? e.message : "network error");
      });
    return () => { live = false; };
  }, [client, devicesNonce]);

  // Resume the owner's last edit: load the saved Test look (the debounced WIP) for the selected camera
  // into the editor when a camera is selected, instead of starting from defaults (#189). 404 (never
  // saved → fresh owner) keeps the editor's defaults. Re-seeds via loadLook (the same channel the
  // library uses); marks it the clean baseline so it doesn't read as an unsaved edit.
  useEffect(() => {
    if (!deviceId) return;
    let live = true;
    client
      .getTestLook(deviceId)
      .then((wip) => {
        if (!live || !wip) return;
        // Don't clobber an in-flight edit: only resume the WIP if the editor is still clean (#189 review).
        const { look: curLook, baseline: curBaseline } = editStateRef.current;
        if (curLook && curBaseline && !sameLook(curLook, curBaseline)) return;
        const editable = wireLookToEditable(wip); // Test look has no committed id — stays a fresh look
        setLoadLook(editable);
        setBaseline(editable);
      })
      .catch((e) => { if (live && isUnauthorized(e)) setCommitMsg({ kind: "error", text: "Sign in to manage Looks." }); });
    return () => { live = false; };
  }, [client, deviceId]);

  // Auto-save the editor WIP as the Test look (debounced) for the selected camera, so a single tap
  // in Look Sync mode pulls the latest edit onto the camera's live Preview (#107). The editor shows
  // the saving/saved/failed status near the preview, so the mirror is visible (not a silent best-effort).
  useEffect(() => {
    if (!deviceId || !look) return;
    const t = setTimeout(() => {
      setTestLook("saving");
      client.saveTestLook(deviceId, editableLookToWire(look))
        .then(() => setTestLook("saved"))
        .catch(() => setTestLook("failed"));
    }, 600);
    return () => clearTimeout(t);
  }, [client, deviceId, look]);

  async function onCommit(): Promise<void> {
    if (!deviceId || !look) return;
    setCommitting(true);
    setCommitMsg(null);
    try {
      const { id } = await client.commitLook(deviceId, editableLookToWire(look));
      // Stamp the server-minted (or preserved) id back into editor state, so a follow-up commit
      // of the SAME Look updates it in place instead of minting a duplicate (#161). The editor
      // owns its Look state, so the page's copy alone is not enough: the editor re-reports its
      // own (old-id) Look on the next render and would undo this. Push the committed Look INTO
      // the editor the way a library load does — seen live when a guest's re-commit forked a
      // third copy because the server-minted fork id never reached the editor.
      const committed = { ...look, id };
      setLook(committed);
      setLoadLook(committed);
      setCommitMsg({ kind: "ok", text: `Committed “${look.name}” — your camera picks it up on its next Sync or Look-Sync tap.` });
      setBaseline(committed); // committing makes the current Look the new clean baseline
      setCommittedAt((n) => n + 1);
    } catch (e) {
      setCommitMsg({ kind: "error", text: e instanceof Error ? e.message : "Commit failed." });
    } finally {
      setCommitting(false);
    }
  }

  // Fetch the selected camera's Reference loop as presigned frame URLs — only when a camera is
  // selected (else the editor hides the Camera-loop source). The editor decodes + animates them.
  const onRequestCameraLoop = deviceId
    ? async (): Promise<{ url: string }[]> => {
        const loop = await client.getReferenceLoop(deviceId);
        return loop.frames.map((f) => ({ url: f.url }));
      }
    : undefined;

  const cameraName = devices.find((d) => d.deviceId === deviceId)?.name ?? (deviceId || undefined);

  // The editor reports its Look on every edit; the first report seeds the baseline (clean state).
  function handleLookChange(l: EditableLook): void {
    setLook(l);
    setBaseline((b) => b ?? l);
  }
  function applyLoad(l: EditableLook): void {
    setLoadLook(l);
    setBaseline(l);
    setPendingLoad(null);
  }
  // Re-open a library Look — but if the editor has unsaved edits, confirm before discarding them.
  function requestLoad(l: EditableLook): void {
    if (baseline && look && !sameLook(look, baseline)) setPendingLoad(l);
    else applyLoad(l);
  }

  return (
    <div className="lookspage">
      {devicesError && (
        <p className="lookspage__devices-err" role="alert" data-testid="devices-error">
          Couldn’t load your cameras ({devicesError}) — Commit is unavailable until they load.{" "}
          <button type="button" className="btn" onClick={() => setDevicesNonce((n) => n + 1)}>
            Retry
          </button>
        </p>
      )}
      {pendingLoad && (
        <div className="lookspage__guard" role="alertdialog" aria-label="Replace your edits?" data-testid="edit-guard">
          <span>Replace your current edits with “{pendingLoad.name}”? Unsaved changes will be lost.</span>
          <button type="button" className="btn btn--danger" onClick={() => applyLoad(pendingLoad)}>Replace</button>
          <button type="button" className="btn" onClick={() => setPendingLoad(null)}>Keep editing</button>
        </div>
      )}
      <LookPortal
        loadLook={loadLook}
        seed={seed}
        onLookChange={handleLookChange}
        onRequestCameraLoop={onRequestCameraLoop}
        cameraName={cameraName}
        devices={devices}
        deviceId={deviceId}
        onSelectDevice={setDeviceId}
        onCommit={onCommit}
        onNewLook={() => requestLoad({ ...DEFAULT_LOOK })}
        committing={committing}
        commitStatus={commitMsg}
        dirty={!!(look && baseline && !sameLook(look, baseline))}
        testLookState={deviceId ? testLook : "idle"}
      />
      <LookLibrary client={client} deviceId={deviceId} hasDevices={devices.length > 0} reloadKey={committedAt} onLoadLook={requestLoad} />
    </div>
  );
}
